/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTheme } from '../theme/ThemeContext';
import { useAlertDialog } from '../components/common/AlertDialogContext';
import ThemedButton from '../components/common/ThemedButton';
import ThemedInput from '../components/common/ThemedInput';
import { inlineButton } from '../components/athlohive/EventFormFields';
import { describeBatchError } from '../components/athlohive/describeBatchError';
import CertificateFileUpload from '../components/athlohive/CertificateFileUpload';
import {
  LIMITS, TEMPLATE_PLACEHOLDERS,
  buildTemplateBatch, hydrateTemplateForm, initialTemplateForm, validateTemplateForm,
} from '../components/athlohive/certificateTemplates';
import {
  Field, Notice, PreviewModal, apiHelper, errorText, templatesKey, useApplication, useTemplates,
} from '../components/athlohive/CertificateTemplateParts';

/**
 * Add or edit one certificate template, on its own page:
 *   /dashboard/applications/:id/certificate-templates/new
 *   /dashboard/applications/:id/certificate-templates/:templateId/edit
 * Saving or Cancel goes back to the list.
 */
export default function CertificateTemplateFormPage() {
  const { id: applicationId, templateId } = useParams();
  const adding = !templateId;
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { showAlertDialog } = useAlertDialog();
  const queryClient = useQueryClient();
  const { application, organizationId } = useApplication(applicationId);
  const templates = useTemplates(applicationId, organizationId);

  const [form, setForm] = useState(adding ? initialTemplateForm() : null);
  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const listPath = `/dashboard/applications/${applicationId}/certificate-templates`;
  const back = () => navigate(listPath);

  // Editing: the template's saved values, once the list has loaded.
  const existing = !adding && templates.isSuccess
    ? templates.data.find((row) => String(row.id) === String(templateId)) : null;
  useEffect(() => {
    if (!adding && existing && !form) setForm(hydrateTemplateForm(existing));
  }, [adding, existing, form]);

  const saveTemplate = useMutation({
    mutationFn: async () => {
      const resp = await apiHelper.apiCall('/arcee/application/table/save/records/', {
        method: 'POST',
        body: JSON.stringify(buildTemplateBatch(form, { organizationId, applicationId: Number(applicationId) })),
      });
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok) throw result;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templatesKey(applicationId, organizationId) });
      showAlertDialog({
        title: adding ? 'Template added' : 'Template updated',
        message: form.is_active
          ? 'Organizers can now pick it for their event activities.'
          : 'It is saved as inactive, so organizers will not see it.',
        type: 'success', okLabel: 'Back to templates', showCancel: false,
        onOk: back,
      });
    },
    onError: (error) => setSubmitError(describeBatchError(error).message),
  });

  const errors = form ? validateTemplateForm(form, templates.data || []) : {};
  const visible = showErrors ? errors : {};
  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((current) => ({ ...current, [key]: value }));
  };
  const submit = () => {
    if (Object.keys(errors).length) { setShowErrors(true); return; }
    setSubmitError(null);
    saveTemplate.mutate();
  };

  const title = adding ? 'Add template' : `Edit “${existing?.name || form?.name || 'template'}”`;

  return (
    <div style={{ padding: 24, maxWidth: 820, margin: '0 auto' }}>
      <div style={{ marginBottom: 6, fontSize: 13, color: '#6b7280' }}>
        <span style={{ cursor: 'pointer' }} onClick={() => navigate(`/dashboard/applications/${applicationId}`)}>
          {application?.name || 'Application'}
        </span>
        {' / '}
        <span style={{ cursor: 'pointer' }} onClick={back}>Certificate Templates</span>
        {` / ${adding ? 'Add' : 'Edit'}`}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={back} aria-label="Back to templates">
          <i className="bi bi-arrow-left" />
        </button>
        <h2 style={{ color: theme.primary, margin: 0 }}>{title}</h2>
      </div>

      {!adding && templates.isLoading && <div style={{ padding: 16 }}>Loading…</div>}
      {!adding && templates.isError && <Notice tone="warn">The certificate templates could not be loaded.</Notice>}
      {!adding && templates.isSuccess && !existing && (
        <Notice tone="warn">
          This template no longer exists. <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={back}>Back to templates</span>
        </Notice>
      )}

      {form && (

        <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 20, background: '#fff' }}>
          <h4 style={{ marginTop: 0, color: theme.primary }}>{form.id ? 'Template details' : 'New template'}</h4>

          <Field title={`Template name * (max ${LIMITS.name})`} error={visible.name}>
            <ThemedInput value={form.name} onChange={set('name')} maxLength={LIMITS.name} error={visible.name} />
          </Field>

          <Field title={`Description (max ${LIMITS.description})`} help={`One line shown under the name in the organizer's picker. ${(form.description || '').length}/${LIMITS.description}`} error={visible.description}>
            <textarea
              value={form.description} onChange={set('description')} maxLength={LIMITS.description} rows={3}
              style={{
                width: '100%', resize: 'vertical', fontFamily: 'inherit', padding: '8px 11px', outline: 'none',
                border: `1.4px solid ${visible.description ? '#d53b3b' : theme.primary}`,
                borderRadius: theme.buttonRadius, background: '#f8fafc', color: theme.text,
              }}
            />
          </Field>

          <Field
            title="Template URL *"
            help="Upload one self-contained .html file (its CSS in a <style> tag, images embedded as data: links); the link is filled in automatically. It is stored as plain text, so it never runs as a web page on the storage server."
          >
            <CertificateFileUpload
              organizationId={organizationId}
              applicationId={Number(applicationId)}
              value={form.template_url}
              error={visible.template_url}
              onUploaded={(url) => setForm((current) => ({ ...current, template_url: url }))}
              onUploadingChange={setUploading}
            />
          </Field>

          <Field title="Active" help="Inactive templates are hidden from organizers.">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginTop: 6 }}>
              <input type="checkbox" checked={form.is_active} onChange={set('is_active')} />
              Organizers can pick this template
            </label>
          </Field>

          <details style={{ marginBottom: 16, fontSize: 13 }}>
            <summary style={{ cursor: 'pointer', color: theme.primary }}>Placeholders a template can use</summary>
            <table style={{ marginTop: 8, borderCollapse: 'collapse' }}>
              <tbody>
                {TEMPLATE_PLACEHOLDERS.map((p) => (
                  <tr key={p.key}>
                    <td style={{ padding: '3px 12px 3px 0', fontFamily: 'monospace' }}>{`{{ ${p.key} }}`}</td>
                    <td style={{ padding: '3px 0', color: '#4b5563' }}>{p.from}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>

          {submitError && <div style={{ ...errorText, fontSize: 14, marginBottom: 12 }}>{submitError}</div>}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <ThemedButton type="button" style={inlineButton} disabled={saveTemplate.isPending || uploading} onClick={submit}>
              {saveTemplate.isPending && <span className="spinner-border spinner-border-sm" aria-hidden="true" style={{ marginRight: 6 }} />}
              {saveTemplate.isPending ? 'Saving…' : uploading ? 'Wait for the upload…' : (form.id ? 'Save changes' : 'Add template')}
            </ThemedButton>
            <button
              type="button" className="btn btn-outline-secondary"
              disabled={!form.template_url || uploading}
              onClick={() => setPreview(form)}
            >
              <i className="bi bi-eye" /> Preview
            </button>
            <button type="button" className="btn btn-link" onClick={back} disabled={uploading || saveTemplate.isPending}>Cancel</button>
          </div>
        </div>
      )}

      {preview && <PreviewModal template={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
